/**
 * Layer 4: RolePersona Context
 *
 * Provides the current role persona and controls power dynamics (WHO evaluates WHOM).
 * Enables the Power Reversal feature where candidates evaluate organizations.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import type { RolePersona, RolePersonaConfig } from '@/types/role-persona'
import { ROLE_PERSONA_CONFIGS } from '@/types/role-persona'

interface RolePersonaContextValue {
  currentPersona: RolePersona
  personaConfig: RolePersonaConfig
  setPersona: (persona: RolePersona) => void
  availablePersonas: RolePersona[]
  canAccessFeature: (featureId: string) => boolean
  canEvaluateOrganizations: boolean
  canPostRoles: boolean
  canReviewCandidates: boolean
}

const RolePersonaContext = createContext<RolePersonaContextValue | undefined>(undefined)

interface RolePersonaProviderProps {
  children: ReactNode
  initialPersona?: RolePersona
}

export function RolePersonaProvider({
  children,
  initialPersona = 'candidate',
}: RolePersonaProviderProps) {
  const [currentPersona, setCurrentPersona] = useState<RolePersona>(initialPersona)

  const setPersona = useCallback((persona: RolePersona) => {
    setCurrentPersona(persona)
    // Persist to localStorage (browser only)
    if (typeof window !== 'undefined') {
      localStorage.setItem('rolePersona', persona)
    }
  }, [])

  const canAccessFeature = useCallback(
    (featureId: string) => {
      const config = ROLE_PERSONA_CONFIGS[currentPersona]
      return config.visibleFeatures.includes(featureId)
    },
    [currentPersona]
  )

  const config = ROLE_PERSONA_CONFIGS[currentPersona]

  const value: RolePersonaContextValue = {
    currentPersona,
    personaConfig: config,
    setPersona,
    availablePersonas: Object.keys(ROLE_PERSONA_CONFIGS) as RolePersona[],
    canAccessFeature,
    canEvaluateOrganizations: config.powerDynamics.canEvaluateOrganizations,
    canPostRoles: config.powerDynamics.canPostRoles,
    canReviewCandidates: config.powerDynamics.canReviewCandidates,
  }

  return (
    <RolePersonaContext.Provider value={value}>
      {children}
    </RolePersonaContext.Provider>
  )
}

export function useRolePersona() {
  const context = useContext(RolePersonaContext)
  if (!context) {
    throw new Error('useRolePersona must be used within RolePersonaProvider')
  }
  return context
}
